"use client"

import { Checkbox } from "@base-ui/react"
import { authIdentity } from "@defuse-protocol/internal-utils"
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/16/solid"
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import { Copy } from "@src/components/CopyButton"
import { MeasuredHeightTransition } from "@src/features/trade/components/position/MeasuredHeightTransition"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { midTruncate } from "@src/shared/utils/display"
import { formatNearcomAddress } from "@src/shared/utils/nearcomAddress"
import { logger } from "@src/utils/logger"
import clsx from "clsx"
import { QRCodeSVG } from "qrcode.react"
import { useCallback, useId, useState } from "react"

type WarningItem = {
  type: "success" | "error"
  title: string
  description?: string
}

const acknowledgementItems: WarningItem[] = [
  {
    type: "success",
    title: "Internal use only!",
    description:
      "Use only to receive assets from other near.com users, on the internal network. Never from a blockchain network.",
  },
  {
    type: "error",
    title: "Your funds will be lost",
    description: "If you send to this address on any blockchain.",
  },
]

const InternalAddressView = () => {
  const [shareCopied, setShareCopied] = useState(false)
  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false)
  const [showAddress, setShowAddress] = useState(false)
  const { state } = useConnectWallet()
  const id = useId()
  const view = showAddress ? "address" : "acknowledgement"

  const internalAddress =
    state.isAuthorized && state.address != null && state.chainType != null
      ? formatNearcomAddress(
          authIdentity.authHandleToIntentsUserId(
            state.address,
            state.chainType
          )
        )
      : undefined

  const handleShareAddress = useCallback(async () => {
    if (!internalAddress) return

    const shareUrl = `${window.location.origin}/contacts?${new URLSearchParams({ address: internalAddress })}`

    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl })
      } catch (err) {
        logger.error(err)
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl)
        setShareCopied(true)
        setTimeout(() => setShareCopied(false), 2000)
      } catch (err) {
        logger.error(err)
      }
    }
  }, [internalAddress])

  if (!internalAddress) return <div>No internal address.</div>

  return (
    <MeasuredHeightTransition view={view}>
      {!showAddress ? (
        <div>
          <ul className="mt-3 space-y-4 pl-3">
            {acknowledgementItems.map(({ type, title, description }) => (
              <ListItem
                key={title}
                type={type}
                title={title}
                description={description}
              />
            ))}
          </ul>

          <div className="-outline-offset-1 mt-5 grid grid-cols-[1.125rem_minmax(0,1fr)] items-start gap-x-2 gap-y-3 rounded-2xl bg-yellow-50 p-3 outline outline-yellow-100 dark:bg-yellow-500/10 dark:outline-yellow-500/10">
            <ExclamationTriangleIcon
              className="mt-px size-4.5 text-yellow-500 dark:text-yellow-300"
              aria-hidden
            />
            <strong className="font-bold text-sm/5 text-yellow-800 dark:text-yellow-100">
              Don&apos;t lose your funds
            </strong>
            <div className="col-start-2 flex items-start gap-2">
              <Checkbox.Root
                id={id}
                checked={acknowledgementChecked}
                onCheckedChange={(checked) =>
                  setAcknowledgementChecked(checked)
                }
                className={clsx(
                  "mt-0.5 size-4 shrink-0 rounded-sm border-2 border-yellow-600",
                  "focus-visible:outline-2 focus-visible:outline-yellow-600 focus-visible:outline-offset-2",
                  "data-checked:border-yellow-600 data-checked:bg-yellow-600 data-checked:text-white",
                  "dark:border-yellow-300 dark:focus-visible:outline-yellow-300",
                  "dark:data-checked:border-yellow-300 dark:data-checked:bg-yellow-300 dark:data-checked:text-yellow-900"
                )}
              >
                <Checkbox.Indicator className="flex size-full items-center justify-center">
                  <CheckIcon className="-mt-0.5 size-4 shrink-0" />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <label
                htmlFor={id}
                className="cursor-pointer font-semibold text-sm/5 text-yellow-800 dark:text-yellow-100"
              >
                I understand funds sent to this address on any blockchain{" "}
                <strong className="font-bold">
                  will be permanently lost.
                </strong>
              </label>
            </div>
          </div>

          <Button
            size="xl"
            className="mt-4"
            fullWidth
            onClick={() => setShowAddress(true)}
            disabled={!acknowledgementChecked}
          >
            Show address
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <div className="font-medium text-gray-500 text-xl/7 dark:text-gray-400">
              {midTruncate(internalAddress, 16)}
            </div>

            <div className="mt-4 flex items-center justify-center rounded-3xl border-4 border-gray-900 bg-green-500 py-6">
              <div className="flex size-52 items-center justify-center rounded-2xl border-4 border-gray-900 bg-white">
                <QRCodeSVG
                  value={internalAddress}
                  size={176}
                  fgColor="#171717"
                  className="size-44"
                />
              </div>
            </div>
          </div>

          <ul className="space-y-4">
            {acknowledgementItems.map(({ type, title, description }) => (
              <ListItem
                key={title}
                type={type}
                title={title}
                description={description}
              />
            ))}
          </ul>

          <div className="space-y-2">
            <Copy text={internalAddress}>
              {(copied) => (
                <Button size="xl" fullWidth>
                  {copied ? "Copied!" : "Copy address"}
                </Button>
              )}
            </Copy>
            <Button
              size="xl"
              variant="secondary"
              fullWidth
              onClick={handleShareAddress}
            >
              {shareCopied ? "Link copied!" : "Share with other near.com users"}
            </Button>
          </div>
        </div>
      )}
    </MeasuredHeightTransition>
  )
}

export default InternalAddressView

const ListItem = ({ type, title, description }: WarningItem) => (
  <li key={title} className="flex items-start gap-2">
    <div className="flex h-5 shrink-0 items-center justify-center">
      {type === "error" && (
        <ExclamationCircleIcon className="size-4.5 text-red-500 dark:text-red-400" />
      )}
      {type === "success" && (
        <CheckCircleIcon className="size-4.5 text-green-600 dark:text-green-500" />
      )}
    </div>
    <div>
      <p className="text-pretty font-semibold text-base/5 text-gray-900 tracking-tight dark:text-white">
        {title}
      </p>
      {description && (
        <p className="mt-0.5 text-pretty font-medium text-gray-500 text-sm/5 dark:text-gray-400">
          {description}
        </p>
      )}
    </div>
  </li>
)
